import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faStethoscope,
  faTimes,
  faCalendarPlus,
  faUserMd,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumb from "../../components/Breadcrumb";
import PatientPortalLayout from "../../components/Patient/PatientPortalLayout";
import styles from "./MyAppointments.module.css";
import "react-toastify/dist/ReactToastify.css";

const MyAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState("upcoming"); // "upcoming" | "past"
  const [loading, setLoading] = useState(true);
  const [patientName, setPatientName] = useState("Patient");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const patientId = localStorage.getItem("userId");
    const fetchAppointments = async () => {
      try {
        if (patientId) {
          const response = await axios.get(
            `/api/appointment/patient/${patientId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAppointments(response.data.appointment || []);

          // Also fetch patient profile for verified display name
          axios.get(`/api/patient/${patientId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then((res) => {
            const p = res.data.patient || {};
            const nameStr = p.firstName ? `${p.firstName} ${p.lastName || ""}`.trim() : (p.name || "Patient");
            setPatientName(nameStr);
          }).catch((err) => console.error("Profile fetch error:", err));
        }
      } catch (error) {
        console.error("Error fetching patient appointments:", error);
        toast.error("Failed to load appointment records");
      } finally {
        setLoading(false);
      }
    };
    fetchAppointments();
  }, [token]);

  const cancelAppointment = async (appointmentId) => {
    try {
      const response = await axios.delete(`/api/appointment/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setAppointments(appointments.filter((app) => app._id !== appointmentId));
        toast.success("Appointment cancelled successfully");
      } else {
        toast.error("Failed to cancel consultation");
      }
    } catch (error) {
      toast.error("Error cancelling consultation");
    }
  };

  function formatTimeToAMPM(time) {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    let period = "AM";
    let formattedHours = parseInt(hours, 10);
    if (formattedHours >= 12) {
      period = "PM";
      if (formattedHours > 12) {
        formattedHours -= 12;
      }
    }
    return `${formattedHours}:${minutes} ${period}`;
  }

  function formatDateAndTime(dateStr, slot) {
    let dFormatted = dateStr || "Scheduled";
    if (dateStr && dateStr.includes("-")) {
      const parts = dateStr.split("-");
      const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
      dFormatted = dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
    const tFormatted =
      slot && slot.startTime ? formatTimeToAMPM(slot.startTime) : "10:00 AM";
    return `${dFormatted} • ${tFormatted}`;
  }

  // Filter based on Segmented Control Tabs
  const filteredAppointments = appointments.filter((app) => {
    const s = (app.status || "scheduled").toLowerCase();
    const isPast = s === "completed" || s === "canceled" || s === "cancelled";
    return activeTab === "upcoming" ? !isPast : isPast;
  });

  const breadcrumbs = [
    { title: "Portal Overview", link: "/patient-dashboard" },
    { title: "My Appointments", link: "/myappointment" },
  ];

  return (
    <PatientPortalLayout
      activePage="appointments"
      patientName={patientName}
      totalAppointments={appointments.length}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumbs Banner */}
      <div className={styles.breadcrumbRow}>
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section style={{ maxWidth: "1000px", margin: "0 auto", width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <h1 style={{ fontSize: "1.85rem", fontWeight: 850, color: "#0f172a", margin: 0, letterSpacing: "-0.03em" }}>
              Appointment Tracking
            </h1>
            <p style={{ color: "#64748b", margin: "4px 0 0 0", fontSize: "0.95rem" }}>
              Review confirmed visits and manage clinical schedules
            </p>
          </div>

          {/* ─── SEGMENTED CONTROL (TABS) ────────────────────────────── */}
          <div className={styles.segContainer}>
            <button
              type="button"
              onClick={() => setActiveTab("upcoming")}
              className={`${styles.segTab} ${
                activeTab === "upcoming" ? styles.segTabActive : ""
              }`}
            >
              Upcoming
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("past")}
              className={`${styles.segTab} ${
                activeTab === "past" ? styles.segTabActive : ""
              }`}
            >
              Past
            </button>
          </div>
        </div>

        {/* ─── APPOINTMENTS FLEX CARDS LIST ──────────────────────────── */}
        {loading ? (
          <p style={{ color: "#64748b", fontStyle: "italic" }}>
            Loading appointments...
          </p>
        ) : filteredAppointments.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {filteredAppointments.map((app) => {
              const docName = app.doctor
                ? app.doctor.firstName
                  ? `Dr. ${app.doctor.firstName} ${app.doctor.lastName}`
                  : "Dr. Clinical Specialist"
                : "Dr. Clinical Specialist";
              const docSpec =
                app.doctor?.specialty || app.disease || "Orthopedic Therapy";
              const status = app.status || "Confirmed";
              const isConfirmed =
                status.toLowerCase() === "scheduled" ||
                status.toLowerCase() === "confirmed";

              return (
                <div className={styles.horizCard} key={app._id}>
                  {/* Left Block: Date & Time */}
                  <div className={styles.horizLeft}>
                    <span className={styles.horizDate}>
                      {formatDateAndTime(app.appointmentDate, app.slot).split(" • ")[0]}
                    </span>
                    <span className={styles.horizTime}>
                      {formatDateAndTime(app.appointmentDate, app.slot).split(" • ")[1]}
                    </span>
                  </div>

                  {/* Middle Block: Doctor, Specialty & Subtle Visit Icon */}
                  <div className={styles.horizMiddle}>
                    <div className={styles.horizDocIconBox}>
                      <FontAwesomeIcon icon={faStethoscope} />
                    </div>
                    <div className={styles.horizDocDetails}>
                      <span className={styles.horizDocName}>{docName}</span>
                      <span className={styles.horizDocSpec}>
                        {docSpec} • Consultation Visit
                      </span>
                    </div>
                  </div>

                  {/* Right Block: Status Pill & Cancel Ghost CTA */}
                  <div className={styles.horizRight}>
                    <span
                      className={
                        isConfirmed
                          ? styles.statusPillGreen
                          : styles.statusPillSlate
                      }
                    >
                      {isConfirmed ? "Confirmed" : status}
                    </span>

                    {activeTab === "upcoming" && (
                      <button
                        type="button"
                        onClick={() => cancelAppointment(app._id)}
                        className={styles.cancelGhostBtn}
                      >
                        <FontAwesomeIcon icon={faTimes} style={{ marginRight: "6px" }} />
                        <span>Cancel</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ─── CLEAN CENTERED EMPTY STATE ───────────────────────────── */
          <div
            style={{
              background: "rgba(255, 255, 255, 0.75)",
              backdropFilter: "blur(12px)",
              border: "1px dashed #cbd5e1",
              borderRadius: "24px",
              padding: "4rem 2rem",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1.25rem",
              marginTop: "1rem",
            }}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "#f1f5f9",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.75rem",
              }}
            >
              <FontAwesomeIcon icon={faCalendarCheck} />
            </div>
            <div>
              <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
                No {activeTab} consultations found
              </h3>
              <p style={{ color: "#64748b", margin: "6px 0 0 0", fontSize: "0.92rem" }}>
                You have no scheduled clinical visits under this category.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/patient-dashboard")}
              style={{
                background: "#2d6a4f",
                color: "#ffffff",
                border: "none",
                borderRadius: "9999px",
                padding: "0.75rem 1.75rem",
                fontWeight: 750,
                fontSize: "0.95rem",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 6px 20px rgba(45, 106, 79, 0.2)",
              }}
            >
              <FontAwesomeIcon icon={faCalendarPlus} />
              <span>Book New Appointment</span>
            </button>
          </div>
        )}
      </section>
    </PatientPortalLayout>
  );
};

export default MyAppointments;
